import { motion } from "framer-motion";
import { Check } from "lucide-react";

const PORTRAIT = "/eddy-portrait.jpg";

const CREDENTIALS = [
  "Bachelor of Accountancy",
  "Master of Business (Accounting & Finance), UTS",
  "MBA Accounting, UBSS",
  "MIPA — Member, Institute of Public Accountants",
  "Registered Tax Agent",
  "Registered ASIC Agent",
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export const About = () => {
  return (
    <section id="about" className="relative py-24 md:py-40 bg-[#0a0a0f]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={reveal}
          className="flex items-center gap-4 mb-16"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-[#c9a84c]">01 — The Principal</span>
          <span className="h-px flex-1 bg-white/10" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden">
              <img
                src={PORTRAIT}
                alt="Eddy Rab, Accountant & Registered Tax Agent"
                className="w-full aspect-[4/5] object-cover grayscale-[0.15] contrast-[1.05]"
                data-testid="about-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/20" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden sm:block bg-[#12121a] border border-white/5 px-6 py-4">
              <p className="font-display text-2xl text-[#e8d9a8] leading-none">Eddy Rab</p>
              <p className="font-body text-xs tracking-[0.2em] uppercase text-[#9ba1a6] mt-2">
                Accountant & Tax Agent
              </p>
            </div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={reveal}
              className="font-display font-light tracking-tight text-[#f8f9fa] text-3xl sm:text-4xl lg:text-5xl leading-tight"
            >
              A single, trusted adviser for every dollar that matters.
            </motion.h2>

            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={reveal}
              custom={1}
              className="font-body text-[#9ba1a6] text-base sm:text-lg font-light leading-relaxed mt-8 max-w-2xl"
            >
              Eddy Rab brings a rare combination of academic depth and practical mastery to
              private and business finances. Working directly with each client — never through
              a call centre — he delivers considered strategy, precise compliance and complete
              discretion. Based in Bankstown, NSW, serving discerning clients across Australia.
            </motion.p>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={reveal}
              custom={2}
              className="mt-12"
            >
              <p className="font-body text-xs tracking-[0.3em] uppercase text-[#c9a84c] mb-6">
                Credentials
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-start gap-3" data-testid="about-credential">
                    <span className="mt-1 shrink-0 grid place-items-center h-5 w-5 rounded-full border border-[#c9a84c]/40">
                      <Check size={11} className="text-[#c9a84c]" />
                    </span>
                    <span className="font-body text-sm text-[#e6e7e9] font-light">{c}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
